import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { authApi } from '../../services/authApi';
import { ApiError } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

type Status = 'verifying' | 'success' | 'error';

/**
 * Email-verification landing page (#209). Reads the single-use token from the
 * URL (`/verify-email?token=…`), posts it, and shows success / failure. On
 * success it refreshes the auth token so the freshly-verified claim lands in
 * the JWT and the dashboard "unverified" banner clears. A missing token
 * short-circuits to an error.
 *
 * Verification is informational at MVP — it never blocks the app, so an error
 * still offers a path back to the dashboard.
 */
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const refreshToken = useAuthStore((s) => s.refreshToken);
  const signIn = useAuthStore((s) => s.signIn);

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  const [message, setMessage] = useState<string>(
    token ? '' : 'This verification link is missing its token.'
  );

  // Guard against React 18 StrictMode double-invoke / re-renders firing the
  // verify request twice (the token is single-use).
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        await authApi.verifyEmail(token);
        // Refresh the token so the new emailVerified=true claim is reflected and
        // the dashboard banner clears. Best-effort: verification already
        // succeeded, so a refresh failure must not flip us to an error state.
        if (refreshToken) {
          try {
            const bundle = await authApi.refresh(refreshToken);
            signIn(bundle);
          } catch {
            /* ignore — the next natural refresh/login will pick up the claim */
          }
        }
        if (!cancelled) {
          setStatus('success');
          setMessage('Your email is verified.');
        }
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        if (err instanceof ApiError && err.httpStatus === 400 && err.serverMessage) {
          setMessage(err.serverMessage);
        } else if (err instanceof ApiError && err.httpStatus === 429) {
          setMessage('Too many attempts. Please wait a minute and try again.');
        } else {
          setMessage('Something went wrong verifying your email. Please try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, refreshToken, signIn]);

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
          Email verification
        </Typography>

        {status === 'verifying' ? (
          <Stack direction="row" spacing={2} alignItems="center" role="status">
            <CircularProgress size={24} aria-hidden="true" />
            <Typography>Verifying your email…</Typography>
          </Stack>
        ) : status === 'success' ? (
          <Stack spacing={2}>
            <Alert severity="success" role="status">
              {message}
            </Alert>
            <Button component={RouterLink} to="/dashboard" variant="contained">
              Go to your dashboard
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity="error" role="alert">
              {message}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              You can request a fresh link from your{' '}
              <MuiLink component={RouterLink} to="/dashboard">
                dashboard
              </MuiLink>
              .
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

export default VerifyEmailPage;
